import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';
import { SeasonPassService } from 'src/app/services/season-pass.service';
import { SeasonPass } from 'src/app/interfaces/abonado';
import { Product } from 'src/app/interfaces/product';

/**
 * Checkout de alta/renovación de un abono de temporada. Misma estructura y
 * estilos que el checkout de entradas (cart-checkout), pero con un solo
 * concepto: el abono que el usuario elige pagar.
 *
 * Los productos ofrecidos son los de la temporada asignados a la categoría del
 * abonado Y al nivel de su butaca (p. ej. con y sin carnet físico); si solo hay
 * uno no se muestra el selector. Se piden al endpoint del abono
 * (`payment/products`) y no al de la temporada, porque el nivel de la butaca lo
 * resuelve el servidor: es él quien sabe traducirlo a la versión vigente del
 * mapa del recinto.
 */
@Component({
  selector: 'app-season-pass-checkout',
  templateUrl: './season-pass-checkout.component.html',
  styleUrls: ['./season-pass-checkout.component.scss']
})
export class SeasonPassCheckoutComponent implements OnInit {

  loaded: boolean = false;
  waiting: boolean = false;
  loadErrorMsg: string | null = null;

  seasonPass: SeasonPass | null = null;
  products: Product[] = [];
  selectedProduct: Product | null = null;
  // Nivel de butaca resuelto por el servidor, que es el que fija qué productos
  // se ofrecen y a qué precio.
  levelName: string | null = null;

  redsysMethod: 'card' | 'xpay' | 'bizum' = 'card';
  // En entradas esto sale de company.payment_method_citylok; el abono no
  // arrastra la compañía completa, así que de momento se ofrecen los tres.
  citylokPaymentMethodsDisabled: boolean = false;

  checkAcceptConditions: boolean = false;

  // Gastos de gestión: los calcula el TPV, aquí solo se muestran.
  gestionPrice: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private seasonPassService: SeasonPassService,
  ) { }

  ngOnInit(): void {
    this.loadSeasonPass();
  }

  /**
   * No hay endpoint de detalle en autoservicio (el `show` por compañía es de
   * admin), así que el abono se busca dentro de los del propio usuario. De
   * paso eso garantiza que solo se pueda pagar un abono propio.
   */
  private loadSeasonPass() {
    const id = +this.route.snapshot.params['id'];

    this.seasonPassService.getMySeasonPasses().subscribe({
      next: (res) => {
        const seasonPass = (res.data || []).find((sp: SeasonPass) => sp.id === id);

        if (!seasonPass) {
          this.loadErrorMsg = 'No hemos encontrado este abono en tu cuenta.';
          this.loaded = true;
          return;
        }

        this.seasonPass = seasonPass;
        this.loadProducts();
      },
      error: () => {
        this.loadErrorMsg = 'No hemos podido cargar tu abono. Vuelve a intentarlo en unos minutos.';
        this.loaded = true;
      }
    });
  }

  /**
   * Opciones que puede pagar este abono: los productos de su temporada
   * asignados a su categoría y al nivel de su butaca. El servidor resuelve el
   * nivel y, si no hay nada que ofrecer, devuelve el motivo (`no_seat` /
   * `level_not_assigned`) para poder explicarlo en vez de mostrar un checkout
   * vacío.
   */
  private loadProducts() {
    const companyId = this.seasonPass!.season?.id_company;

    if (!companyId) {
      this.loadErrorMsg = 'Este abono no tiene una temporada válida asociada.';
      this.loaded = true;
      return;
    }

    this.seasonPassService.getAvailableProducts(companyId, this.seasonPass!.id).subscribe({
      next: (res) => {
        this.levelName = res?.data?.level_name || null;
        this.products = (res?.data?.products || []).filter((p: Product) => p.active !== false);

        if (!this.products.length) {
          this.loadErrorMsg = res?.data?.reason === 'no_seat'
            ? 'Tu abono todavía no tiene butaca asignada. Ponte en contacto con tu club para que te la asigne y podrás pagarlo aquí.'
            // Fallo de configuración del club, no algo que el abonado pueda resolver.
            : 'No hay ningún importe configurado para el nivel de tu butaca todavía.';
          this.loaded = true;
          return;
        }

        this.selectProduct(this.products[0]);
        this.loaded = true;
      },
      error: () => {
        this.loadErrorMsg = 'No hemos podido cargar el importe de tu abono. Vuelve a intentarlo en unos minutos.';
        this.loaded = true;
      }
    });
  }

  selectProduct(product: Product) {
    this.selectedProduct = product;
    // Los gastos de gestión los fija el TPV por producto, así que cambian con
    // la opción elegida.
    this.gestionPrice = product.fee != null ? +product.fee : 0;
  }

  selectPaymentMethod(method: 'card' | 'xpay' | 'bizum') {
    if (method !== 'card' && this.citylokPaymentMethodsDisabled) {
      return;
    }
    this.redsysMethod = method;
  }

  get subtotal(): number {
    return this.selectedProduct ? +this.selectedProduct.price : 0;
  }

  get totalPrice(): number {
    return this.subtotal + this.gestionPrice;
  }

  get seatDisplay(): string | null {
    if (!this.seasonPass?.seat) return null;
    const parts: string[] = [];
    // El nombre del nivel lo da el servidor al resolver la butaca; `id_section`
    // es una PK del mapa y no se le muestra al abonado.
    if (this.levelName) parts.push(this.levelName);
    if (this.seasonPass.row) parts.push(`Fila ${this.seasonPass.row}`);
    if (this.seasonPass.seat) parts.push(`Asiento ${this.seasonPass.seat}`);
    return parts.join(', ');
  }

  pay() {
    if (!this.checkAcceptConditions || !this.selectedProduct || !this.seasonPass || this.waiting) return;

    const companyId = this.seasonPass.season!.id_company;
    this.waiting = true;

    this.seasonPassService.payOnline(companyId, this.seasonPass.id, {
      quantity: 1,
      redsys_method: this.redsysMethod,
      id_product: this.selectedProduct.id,
    }).subscribe({
      next: (res) => {
        const redsys = res.data?.redsys;

        if (!redsys?.Ds_MerchantParameters) {
          this.waiting = false;
          this.openInfoCard('No hemos podido conectar con la pasarela de pago. Inténtalo de nuevo en unos minutos.');
          return;
        }

        // La página de retorno no recibe a qué renovación corresponde el pago,
        // así que se deja anotado para poder llamar luego a payment/confirm.
        const renewal = res.data?.renewal;
        if (renewal?.id_invoicing) {
          localStorage.setItem('season_pass_pending_invoicing', String(renewal.id_invoicing));
          localStorage.setItem('season_pass_pending_id', String(this.seasonPass!.id));
        }

        this.redirectToRedsys(redsys);
      },
      error: (err) => {
        this.waiting = false;
        this.openInfoCard(err?.error?.message || 'No hemos podido iniciar el pago. Inténtalo de nuevo.');
      }
    });
  }

  /**
   * Redsys exige que el navegador llegue por POST con los tres campos
   * firmados, así que se construye un formulario y se envía — igual que en el
   * checkout de entradas.
   */
  private redirectToRedsys(paymentData: any) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = paymentData.url;

    const fields: { [key: string]: string } = {
      Ds_SignatureVersion: paymentData.Ds_SignatureVersion,
      Ds_MerchantParameters: paymentData.Ds_MerchantParameters,
      Ds_Signature: paymentData.Ds_Signature,
    };

    Object.keys(fields).forEach((name) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  private openInfoCard(text: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text }
    });
  }

  goBack() {
    this.location.back();
  }

  goToMyMemberships() {
    this.router.navigate(['/profile/abonos']);
  }
}
